/**
 * Accessibility enhancements for the map widget.
 * Adapted for MapLibre GL JS.
 */

export function applyA11y(container, map, markers, config) {
  // 1. Label the map region
  container.setAttribute('role', 'region');
  container.setAttribute('aria-label', 'Interactive location map');

  // 2. Label the map element itself
  const mapEl = container.querySelector('.pm-map');
  if (mapEl) {
    mapEl.setAttribute('role', 'application');
    mapEl.setAttribute('aria-label', 'Map showing clinic locations. Use tab to navigate markers.');
    mapEl.setAttribute('aria-roledescription', 'interactive map');
  }

  // 3. Label zoom controls (MapLibre class names)
  const zoomIn = container.querySelector('.maplibregl-ctrl-zoom-in');
  const zoomOut = container.querySelector('.maplibregl-ctrl-zoom-out');
  if (zoomIn) zoomIn.setAttribute('aria-label', 'Zoom in');
  if (zoomOut) zoomOut.setAttribute('aria-label', 'Zoom out');

  // 4. Escape key closes popups and returns focus to the marker
  let lastFocusedElement = null;

  markers.forEach(({ element, popup }) => {
    if (!popup) return;
    popup.on('open', () => {
      lastFocusedElement = element;
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Close any open popup
      markers.forEach(({ popup }) => {
        if (popup && popup.isOpen()) popup.remove();
      });

      if (lastFocusedElement) {
        lastFocusedElement.focus();
        lastFocusedElement = null;
      }
    }
  });

  // 5. Skip link for keyboard users to bypass the map
  const skipTarget = document.createElement('span');
  skipTarget.id = 'pm-after-map-' + Math.random().toString(36).substr(2, 6);
  skipTarget.setAttribute('tabindex', '-1');
  container.parentNode.insertBefore(skipTarget, container.nextSibling);

  const skipLink = document.createElement('a');
  skipLink.href = '#' + skipTarget.id;
  skipLink.className = 'pm-skip-link';
  skipLink.textContent = 'Skip past map';
  skipLink.style.cssText = `
    position: absolute;
    left: -9999px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: 10000;
    padding: 8px 16px;
    background: #fff;
    color: #222;
    font-size: 14px;
    border: 2px solid ${config.brandColor};
    border-radius: 4px;
    text-decoration: none;
  `;
  skipLink.addEventListener('focus', () => {
    skipLink.style.cssText = `
      position: absolute;
      left: 8px;
      top: 8px;
      width: auto;
      height: auto;
      overflow: visible;
      z-index: 10000;
      padding: 8px 16px;
      background: #fff;
      color: #222;
      font-size: 14px;
      border: 2px solid ${config.brandColor};
      border-radius: 4px;
      text-decoration: none;
    `;
  });
  skipLink.addEventListener('blur', () => {
    skipLink.style.left = '-9999px';
    skipLink.style.width = '1px';
    skipLink.style.height = '1px';
    skipLink.style.overflow = 'hidden';
  });

  container.style.position = 'relative';
  container.insertBefore(skipLink, container.firstChild);

  // 6. Attribution links: ensure they open safely
  const attrLinks = container.querySelectorAll('.maplibregl-ctrl-attrib a');
  attrLinks.forEach(link => {
    link.setAttribute('rel', 'noopener noreferrer');
  });
}
